import {
  ModuleDraft,
  Lesson,
  LessonMaterial,
  MultipleChoiceQuestion,
  FeedbackQuestion,
  mapAnswerFormatToFeedbackType
} from '../types';

const ensureArray = (value: any) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const createLessonFromResponse = (lesson: any, moduleIndex: number, lessonIndex: number): Lesson => {
  const materials = ensureArray(lesson?.materials || lesson?.resources).map((material: any, materialIndex: number) => ({
    id: material?.id || material?._id || `material-${moduleIndex}-${lessonIndex}-${materialIndex}`,
    title: material?.title || material?.name || `Resource ${materialIndex + 1}`,
    reference: material?.reference || material?.url || '',
    thumbnail: material?.thumbnail,
    articleId: material?.articleId,
    readTime: material?.readTime,
    source: 'ai'
  })) as LessonMaterial[];

  const feedbackQuestions = ensureArray(lesson?.feedbackQuestions).map((question: any, questionIndex: number) => ({
    id: question?.id || question?._id || `feedback-${moduleIndex}-${lessonIndex}-${questionIndex}`,
    prompt: question?.prompt || question?.question || `Question ${questionIndex + 1}`,
    type: mapAnswerFormatToFeedbackType(question?.menteeAnsType || question?.type)
  })) as FeedbackQuestion[];

  const mcqBlock = Array.isArray(lesson?.multipleChoiceQuestions) ? lesson.multipleChoiceQuestions : lesson?.mcq ? [lesson.mcq] : [];

  const multipleChoiceQuestions = mcqBlock.map((mcq: any, mcqIndex: number) => ({
    id: mcq?.id || mcq?._id || `mcq-${moduleIndex}-${lessonIndex}-${mcqIndex}`,
    prompt: mcq?.prompt || mcq?.question || `MCQ ${mcqIndex + 1}`,
    options: ensureArray(mcq?.options).map((option: any, optionIndex: number) => ({
      id: option?.id || option?._id || `mcq-${moduleIndex}-${lessonIndex}-${mcqIndex}-${optionIndex}`,
      label: option?.label || option?.text || option || `Option ${optionIndex + 1}`
    }))
  })) as MultipleChoiceQuestion[];

  return {
    id: lesson?._id || lesson?.taskId || `module-${moduleIndex}-lesson-${lessonIndex}`,
    title: lesson?.title || lesson?.taskName || `Lesson ${lessonIndex + 1}`,
    description: lesson?.content || lesson?.taskDescription || '',
    materials,
    feedbackQuestions,
    multipleChoiceQuestions,
    status: lesson?.status,
    usedRag: lesson?.usedRag,
    generatedAt: lesson?.generatedAt ?? lesson?.createdAt ?? null
  };
};

export const structureResponseToManualModules = (structure: any): ModuleDraft[] => {
  const modules =
    structure?.programStructure?.modules ||
    structure?.modules ||
    structure?.data?.modules ||
    structure?.data?.levels ||
    structure?.levels ||
    [];

  return ensureArray(modules).map((module: any, moduleIndex: number) => ({
    id: module?._id || module?.levelId || `module-${moduleIndex}`,
    title: module?.title || module?.levelName || `Module ${moduleIndex + 1}`,
    objective: module?.levelObjective || module?.description || '',
    lessons: ensureArray(module?.lessons || module?.tasks).map((lesson: any, lessonIndex: number) => ({
      id: lesson?._id || lesson?.taskId || `module-${moduleIndex}-lesson-${lessonIndex}`,
      title: lesson?.title || lesson?.taskName || `Lesson ${lessonIndex + 1}`,
      description: lesson?.taskDescription || lesson?.content || '',
      materials: [],
      feedbackQuestions: [],
      multipleChoiceQuestions: []
    }))
  }));
};

export const modulesToContentPayload = (modules: ModuleDraft[]) =>
  modules.map((module, moduleIndex) => ({
    title: module.title || `Module ${moduleIndex + 1}`,
    lessons: module.lessons.map((lesson, lessonIndex) => ({
      title: lesson.title || `Lesson ${lessonIndex + 1}`
    }))
  }));

export const contentResponseToManualModules = (payload: any): ModuleDraft[] => {
  const modules =
    payload?.modules ||
    payload?.programContent?.modules ||
    payload?.data?.modules ||
    payload?.data?.levels ||
    payload?.levels ||
    [];

  return ensureArray(modules).map((module: any, moduleIndex: number) => ({
    id: module?._id || module?.levelId || `module-${moduleIndex}`,
    title: module?.title || module?.levelName || `Module ${moduleIndex + 1}`,
    objective: module?.levelObjective || module?.description || '',
    lessons: ensureArray(module?.lessons || module?.tasks).map((lesson: any, lessonIndex: number) =>
      createLessonFromResponse(lesson, moduleIndex, lessonIndex)
    ),
    status: module?.status,
    completedLessonsCount: module?.completedLessonsCount,
    totalLessonsCount: module?.totalLessonsCount || ensureArray(module?.lessons || module?.tasks).length
  }));
};
